'use client';

import { c as _c } from "react/compiler-runtime";
import { domAnimation, LazyMotion, m, useReducedMotion } from 'motion/react';
import { useTranslations } from 'next-intl';
import { AnimatedButton } from '@/components/ui/animated-button';
import { AnimatedNumber } from '@/components/ui/animated-number';
import { Icon } from '@/components/ui/icon';
import { HelpCircleIcon, Search01Icon } from '@/components/ui/icon/glyphs';
import { Panel, PanelContent } from '@/components/ui/panel';
import { Link } from '@/i18n/navigation';
import { daysToNextTier, levelForDays, nextTier, progressToNextTier, STREAK_TIERS, streakBarModel, tierBgClass, tierForLevel, tierStrokeClass, tierTextClass } from '@/lib/streaks';
import { cn } from '@/lib/ui/utils';
import { TierEmblem } from './tier-emblem';
const RING_R = 26;
const RING_C: number = 2 * Math.PI * RING_R;
export interface StreakWidgetViewProps {
  currentStreak: number;
  longestStreak: number;
  onViewCalendar: () => void;
  onViewLevels: () => void;
  /** Opens the how-it-works dialog. Optional so presentation-only previews can omit it. */
  onShowHelp?: () => void;
  /** Mirrors the how-it-works dialog open state for `aria-expanded`. */
  helpOpen?: boolean;
  /** Destination for the empty-state browse CTA (rendered as a prefetched `<Link>`). */
  browseHref: string;
  /** Share trigger rendered to the left of "View calendar" (active state
   *  only). Injected by the data wrapper so this view stays presentational
   *  — the share modal needs the user id + network. */
  shareSlot?: React.ReactNode;
}

/** Pure presentation of the portfolio streak card — no data fetching. */
export function StreakWidgetView(t0) {
  const $ = _c(101);
  const {
    currentStreak,
    longestStreak,
    onViewCalendar,
    onViewLevels,
    onShowHelp,
    helpOpen: t1,
    browseHref,
    shareSlot
  } = t0;
  const helpOpen = t1 === undefined ? false : t1;
  const t = useTranslations("streaks");
  const reduce = useReducedMotion();
  let T0;
  let T1;
  let none;
  let t10;
  let t11;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== currentStreak || $[1] !== helpOpen || $[2] !== longestStreak || $[3] !== onShowHelp || $[4] !== onViewLevels || $[5] !== reduce || $[6] !== t) {
    const level = levelForDays(currentStreak);
    none = currentStreak === 0;
    const tier = tierForLevel(level);
    const next = none ? STREAK_TIERS[0] : nextTier(level);
    const toNext = none ? 1 : daysToNextTier(currentStreak, level) ?? 0;
    const progress = none ? 0 : progressToNextTier(currentStreak, level);
    const dash = progress * RING_C;
    const journey = STREAK_TIERS.slice(Math.max(0, level - 4), Math.max(level, 0));
    const bar = none ? null : streakBarModel(currentStreak, level);
    T1 = Panel;
    t11 = "h-full";
    T0 = PanelContent;
    t9 = "flex h-full flex-col gap-4";
    let t12;
    if ($[20] !== t) {
      t12 = t("title");
      $[20] = t;
      $[21] = t12;
    } else {
      t12 = $[21];
    }
    const t13 = onShowHelp === undefined;
    let t14;
    if ($[22] !== t) {
      t14 = t("how_it_works.title");
      $[22] = t;
      $[23] = t14;
    } else {
      t14 = $[23];
    }
    let t15;
    if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
      t15 = <Icon icon={HelpCircleIcon} className="size-3.5" aria-hidden={true} />;
      $[24] = t15;
    } else {
      t15 = $[24];
    }
    let t16;
    if ($[25] !== helpOpen || $[26] !== onShowHelp || $[27] !== t13 || $[28] !== t14) {
      t16 = <button type="button" onClick={onShowHelp} disabled={t13} aria-label={t14} aria-haspopup="dialog" aria-expanded={helpOpen} className="text-foreground-tertiary transition-colors hover:text-foreground-secondary">{t15}</button>;
      $[25] = helpOpen;
      $[26] = onShowHelp;
      $[27] = t13;
      $[28] = t14;
      $[29] = t16;
    } else {
      t16 = $[29];
    }
    let t17;
    if ($[30] !== t12 || $[31] !== t16) {
      t17 = <div className="flex items-center gap-1.5 text-body-lg font-semibold">{t12}{t16}</div>;
      $[30] = t12;
      $[31] = t16;
      $[32] = t17;
    } else {
      t17 = $[32];
    }
    let t18;
    if ($[33] !== t) {
      t18 = t("view_levels");
      $[33] = t;
      $[34] = t18;
    } else {
      t18 = $[34];
    }
    let t19;
    if ($[35] !== onViewLevels || $[36] !== t18) {
      t19 = <button type="button" onClick={onViewLevels} className="text-caption font-medium text-foreground-tertiary transition-colors hover:text-foreground-secondary">{t18}</button>;
      $[35] = onViewLevels;
      $[36] = t18;
      $[37] = t19;
    } else {
      t19 = $[37];
    }
    if ($[38] !== t17 || $[39] !== t19) {
      t10 = <div className="flex items-center justify-between">{t17}{t19}</div>;
      $[38] = t17;
      $[39] = t19;
      $[40] = t10;
    } else {
      t10 = $[40];
    }
    t7 = "my-auto flex flex-col gap-4";
    let t20;
    if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
      t20 = <circle cx="30" cy="30" r={RING_R} fill="none" strokeWidth="3.5" className="stroke-background-tertiary" />;
      $[41] = t20;
    } else {
      t20 = $[41];
    }
    const t21 = none ? null : <circle cx="30" cy="30" r={RING_R} fill="none" strokeWidth="3.5" strokeLinecap="round" strokeDasharray={`${dash} ${RING_C}`} transform="rotate(-90 30 30)" className={tierStrokeClass(level)} />;
    let t22;
    if ($[42] !== t21) {
      t22 = <svg viewBox="0 0 60 60" className="absolute inset-0 size-full" role="presentation" aria-hidden="true">{t20}{t21}</svg>;
      $[42] = t21;
      $[43] = t22;
    } else {
      t22 = $[43];
    }
    const t23 = <span className="relative grid size-24 shrink-0 place-items-center">{t22}<TierEmblem level={level} size={52} /></span>;
    let t24;
    if ($[44] !== currentStreak) {
      t24 = <AnimatedNumber value={currentStreak} format={_temp} className="font-mono text-display font-bold leading-none" />;
      $[44] = currentStreak;
      $[45] = t24;
    } else {
      t24 = $[45];
    }
    let t25;
    if ($[46] !== currentStreak || $[47] !== t) {
      t25 = t("days_unit", {
        count: currentStreak
      });
      $[46] = currentStreak;
      $[47] = t;
      $[48] = t25;
    } else {
      t25 = $[48];
    }
    let t26;
    if ($[49] !== t25) {
      t26 = <span className="text-body-lg text-foreground-secondary">{t25}</span>;
      $[49] = t25;
      $[50] = t26;
    } else {
      t26 = $[50];
    }
    let t27;
    if ($[51] !== t24 || $[52] !== t26) {
      t27 = <div className="flex items-baseline justify-center gap-2">{t24}{t26}</div>;
      $[51] = t24;
      $[52] = t26;
      $[53] = t27;
    } else {
      t27 = $[53];
    }
    const t28 = none ? <span className="font-semibold text-foreground-secondary">{t("no_streak_yet")}</span> : <><span className={cn("font-semibold", tierTextClass(level))}>{tier?.name}</span><span className="text-foreground-tertiary">{" \xB7 "}{t("level_of", {
          level
        })}</span></>;
    let t29;
    if ($[54] !== t28) {
      t29 = <div className="mt-2 text-caption">{t28}</div>;
      $[54] = t28;
      $[55] = t29;
    } else {
      t29 = $[55];
    }
    let t30;
    if ($[56] !== t27 || $[57] !== t29) {
      t30 = <div>{t27}{t29}</div>;
      $[56] = t27;
      $[57] = t29;
      $[58] = t30;
    } else {
      t30 = $[58];
    }
    let t31;
    if ($[59] !== longestStreak || $[60] !== none || $[61] !== t) {
      t31 = none ? null : <div className="text-micro text-foreground-tertiary">{t("longest")}{" "}<span className="font-mono font-semibold text-foreground-secondary">{longestStreak}</span></div>;
      $[59] = longestStreak;
      $[60] = none;
      $[61] = t;
      $[62] = t31;
    } else {
      t31 = $[62];
    }
    if ($[63] !== t23 || $[64] !== t30 || $[65] !== t31) {
      t8 = <div className="flex flex-col items-center gap-3 text-center">{t23}{t30}{t31}</div>;
      $[63] = t23;
      $[64] = t30;
      $[65] = t31;
      $[66] = t8;
    } else {
      t8 = $[66];
    }
    const t32 = bar !== null && bar.kind === "pips" ? Array.from({
      length: bar.total
    }, (_, i) => ({
      key: `pip-${i}`,
      filled: i < bar.filled
    })).map(pip => <span key={pip.key} className={cn("h-1.5 flex-1 rounded-pill", pip.filled ? tierBgClass(level) : "bg-background-tertiary")} />) : <span className="h-1.5 flex-1 overflow-hidden rounded-pill bg-background-tertiary"><LazyMotion features={domAnimation}><m.span className={cn("block h-full origin-left rounded-pill", tierBgClass(level))} initial={false} animate={{
          scaleX: bar?.ratio ?? 0
        }} transition={reduce ? {
          duration: 0
        } : {
          duration: 0.25,
          ease: "easeOut"
        }} /></LazyMotion></span>;
    if ($[67] !== t32) {
      t6 = <div className="flex gap-1">{t32}</div>;
      $[67] = t32;
      $[68] = t6;
    } else {
      t6 = $[68];
    }
    t4 = "mt-2 flex items-center justify-between gap-3 text-micro";
    const t33 = none ? t("start_trading") : journey.map((j, i_0) => <span key={j.level}>{i_0 > 0 ? " \xB7 " : ""}<span className={j.level === level ? cn("font-semibold", tierTextClass(level)) : ""}>{j.name}</span></span>);
    if ($[69] !== t33) {
      t5 = <span className="min-w-0 truncate text-foreground-tertiary">{t33}</span>;
      $[69] = t33;
      $[70] = t5;
    } else {
      t5 = $[70];
    }
    t2 = "shrink-0 whitespace-nowrap";
    t3 = next ? <span className={cn("font-semibold", tierTextClass(none ? 1 : level))}>{t("days_to_next", {
        count: toNext,
        tier: next.name
      })}</span> : <span className="text-foreground-tertiary">{t("max_tier_reached")}</span>;
    $[0] = currentStreak;
    $[1] = helpOpen;
    $[2] = longestStreak;
    $[3] = onShowHelp;
    $[4] = onViewLevels;
    $[5] = reduce;
    $[6] = t;
    $[7] = T0;
    $[8] = T1;
    $[9] = none;
    $[10] = t10;
    $[11] = t11;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    T0 = $[7];
    T1 = $[8];
    none = $[9];
    t10 = $[10];
    t11 = $[11];
    t2 = $[12];
    t3 = $[13];
    t4 = $[14];
    t5 = $[15];
    t6 = $[16];
    t7 = $[17];
    t8 = $[18];
    t9 = $[19];
  }
  let t12;
  if ($[71] !== t2 || $[72] !== t3) {
    t12 = <span className={t2}>{t3}</span>;
    $[71] = t2;
    $[72] = t3;
    $[73] = t12;
  } else {
    t12 = $[73];
  }
  let t13;
  if ($[74] !== t12 || $[75] !== t4 || $[76] !== t5) {
    t13 = <div className={t4}>{t5}{t12}</div>;
    $[74] = t12;
    $[75] = t4;
    $[76] = t5;
    $[77] = t13;
  } else {
    t13 = $[77];
  }
  let t14;
  if ($[78] !== t13 || $[79] !== t6) {
    t14 = <div>{t6}{t13}</div>;
    $[78] = t13;
    $[79] = t6;
    $[80] = t14;
  } else {
    t14 = $[80];
  }
  let t15;
  if ($[81] !== t14 || $[82] !== t7 || $[83] !== t8) {
    t15 = <div className={t7}>{t8}{t14}</div>;
    $[81] = t14;
    $[82] = t7;
    $[83] = t8;
    $[84] = t15;
  } else {
    t15 = $[84];
  }
  let t16;
  if ($[85] !== browseHref || $[86] !== none || $[87] !== onViewCalendar || $[88] !== shareSlot || $[89] !== t) {
    t16 = none ? <AnimatedButton variant="primary" size="xl" className="w-full" prefix={Search01Icon} asChild={true}><Link href={browseHref}>{t("browse_markets")}</Link></AnimatedButton> : <div className="@container/streak-actions flex min-w-0 items-center gap-2"><AnimatedButton variant="secondary" size="xl" className="min-w-0 flex-1 shrink px-4 @[20rem]/streak-actions:px-8" onClick={onViewCalendar}><span className="min-w-0 truncate">{t("view_calendar")}</span></AnimatedButton>{shareSlot}</div>;
    $[85] = browseHref;
    $[86] = none;
    $[87] = onViewCalendar;
    $[88] = shareSlot;
    $[89] = t;
    $[90] = t16;
  } else {
    t16 = $[90];
  }
  let t17;
  if ($[91] !== T0 || $[92] !== t10 || $[93] !== t15 || $[94] !== t16 || $[95] !== t9) {
    t17 = <T0 className={t9}>{t10}{t15}{t16}</T0>;
    $[91] = T0;
    $[92] = t10;
    $[93] = t15;
    $[94] = t16;
    $[95] = t9;
    $[96] = t17;
  } else {
    t17 = $[96];
  }
  let t18;
  if ($[97] !== T1 || $[98] !== t11 || $[99] !== t17) {
    t18 = <T1 className={t11}>{t17}</T1>;
    $[97] = T1;
    $[98] = t11;
    $[99] = t17;
    $[100] = t18;
  } else {
    t18 = $[100];
  }
  return t18;
}
function _temp(n) {
  return String(Math.floor(n));
}